import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X, PhoneForwarded } from 'lucide-react';
import { transferTargetsAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// Inline CALL TRANSFER DIRECTORY CRUD. Each row is a person/department the AI can hand a
// live caller to — warm (hold → call them → check availability → bridge) or blind (forward
// straight through). Soft-delete (PUT active=false). The AI matches caller intent to a row
// via name / department / keywords, so keep those descriptive.

const EMPTY = {
  name: '', phoneE164: '', department: '', keywordsText: '',
  voicemailE164: '', allowWarm: true, allowBlind: true
};

const E164 = /^\+[1-9]\d{6,14}$/;

// UI holds keywords as a comma string; API wants an array.
//
// department / voicemailE164 send NULL, not undefined, when emptied. JSON.stringify
// drops undefined keys, and the PUT is a `.partial()` schema — so an absent key is
// simply not written and the old value survives. Blanking a decommissioned
// voicemail number would appear to save and keep routing callers to a dead line.
// Both fields are `.nullable()` server-side, so null is the correct clear.
const toPayload = (d) => ({
  name: d.name.trim(),
  phoneE164: d.phoneE164.trim(),
  department: d.department.trim() || null,
  keywords: (d.keywordsText || '').split(',').map((s) => s.trim()).filter(Boolean),
  voicemailE164: d.voicemailE164.trim() || null,
  allowWarm: d.allowWarm,
  allowBlind: d.allowBlind
});

const toDraft = (t) => ({
  name: t.name || '',
  phoneE164: t.phoneE164 || '',
  department: t.department || '',
  keywordsText: (t.keywords || []).join(', '),
  voicemailE164: t.voicemailE164 || '',
  allowWarm: t.allowWarm ?? true,
  allowBlind: t.allowBlind ?? true
});

function Fields({ d, set }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          className="field-input"
          placeholder="Name (e.g. Brian Southers)"
          value={d.name}
          onChange={(e) => set({ ...d, name: e.target.value })}
          autoFocus
        />
        <input
          className="field-input"
          placeholder="Phone to reach them — E.164 (+17372728567)"
          value={d.phoneE164}
          onChange={(e) => set({ ...d, phoneE164: e.target.value })}
        />
        <input
          className="field-input"
          placeholder="Department / role (optional, e.g. Sales)"
          value={d.department}
          onChange={(e) => set({ ...d, department: e.target.value })}
        />
        <input
          className="field-input"
          placeholder="Voicemail number (optional, E.164)"
          value={d.voicemailE164}
          onChange={(e) => set({ ...d, voicemailE164: e.target.value })}
        />
      </div>
      <input
        className="field-input"
        placeholder="Match keywords, comma-separated (e.g. pricing, demo, owner)"
        value={d.keywordsText}
        onChange={(e) => set({ ...d, keywordsText: e.target.value })}
      />
      <div className="flex items-center gap-5 text-sm text-stone-300">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={d.allowWarm} onChange={(e) => set({ ...d, allowWarm: e.target.checked })} />
          Warm transfer (check availability first)
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={d.allowBlind} onChange={(e) => set({ ...d, allowBlind: e.target.checked })} />
          Blind forward
        </label>
      </div>
      {/* With neither mode allowed the AI drops the row from its directory entirely
          (_transfer_directory_block skips any target with no modes), so the person
          is on this screen and invisible to the AI. Say so instead of saving a row
          that silently does nothing. */}
      {!d.allowWarm && !d.allowBlind && (
        <p className="rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          With both turned off the AI can’t transfer to this person at all — they’ll be
          left out of its directory entirely. Turn one on, or remove them.
        </p>
      )}
    </>
  );
}

export default function TransferDirectoryEditor() {
  const { user } = useAuth();
  const canWrite = user?.role === 'admin'; // writes are admin-only (server 403s otherwise)
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({}); // id -> draft
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await transferTargetsAPI.list();
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load transfer directory.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const validate = (d) => {
    if (!d.name.trim()) return 'Name is required.';
    if (!E164.test(d.phoneE164.trim())) return 'Phone must be E.164 (e.g. +17372728567).';
    if (d.voicemailE164.trim() && !E164.test(d.voicemailE164.trim())) return 'Voicemail number must be E.164.';
    // The API accepts this happily and the AI then ignores the row — a saved entry
    // that does nothing is worse than a rejected one.
    if (!d.allowWarm && !d.allowBlind) {
      return 'Allow at least one transfer type — with both off the AI can’t reach this person.';
    }
    return '';
  };

  const create = async () => {
    const v = validate(creating);
    if (v) { setError(v); return; }
    try {
      const r = await transferTargetsAPI.create(toPayload(creating));
      setItems((list) => [...list, r.data]);
      setCreating(EMPTY);
      setAdding(false);
      setError('');
    } catch (err) {
      setError(err.message || 'Create failed.');
    }
  };

  const startEdit = (t) => setEditing((e) => ({ ...e, [t.id]: toDraft(t) }));
  const cancelEdit = (id) => setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    const v = validate(draft);
    if (v) { setError(v); return; }
    try {
      const r = await transferTargetsAPI.update(id, toPayload(draft));
      setItems((list) => list.map((i) => (i.id === id ? r.data : i)));
      cancelEdit(id);
      setError('');
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
  };

  const remove = async (id) => {
    if (!confirm('Remove this person from the transfer directory?')) return;
    try {
      await transferTargetsAPI.remove(id);
      setItems((list) => list.filter((i) => i.id !== id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl text-stone-100">Transfer directory</h2>
          <p className="text-sm text-stone-400 mt-1">
            Who the AI can hand a live caller to. On a <strong>warm transfer</strong> it puts
            the caller on hold, calls the person to check they're available, then bridges them.
            The AI picks the right person from the caller's request — keep names, departments,
            and keywords descriptive.
          </p>
        </div>
        {canWrite && !adding && (
          <button type="button" className="btn-ghost" onClick={() => { setAdding(true); setError(''); }}>
            <Plus size={14} /> Add person
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {adding && (
        <div className="mb-4 rounded-xl border border-alfred-gold/30 p-4 space-y-3">
          <Fields d={creating} set={setCreating} />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); setError(''); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No one in the directory yet. Add a person the AI can transfer callers to.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((t) => {
            const draft = editing[t.id];
            if (draft) {
              return (
                <li key={t.id} className="py-4 space-y-3">
                  <Fields d={draft} set={(nd) => setEditing((s) => ({ ...s, [t.id]: nd }))} />
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary" onClick={() => saveEdit(t.id)}>
                      <Save size={14} /> Save
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => cancelEdit(t.id)}>
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={t.id} className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <PhoneForwarded size={15} className="text-alfred-gold shrink-0" />
                      <span className="text-stone-100 font-medium">{t.name}</span>
                      {t.department && (
                        <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                          {t.department}
                        </span>
                      )}
                      <span className="text-sm text-stone-400">{t.phoneE164}</span>
                      {t.allowWarm && (
                        <span className="badge bg-emerald-900/40 text-emerald-200 border border-emerald-800/50 text-[10px]">warm</span>
                      )}
                      {t.allowBlind && (
                        <span className="badge bg-sky-900/40 text-sky-200 border border-sky-800/50 text-[10px]">blind</span>
                      )}
                      {/* With neither mode the AI drops this person from its directory.
                          The row otherwise looks completely normal in this list. */}
                      {!t.allowWarm && !t.allowBlind && (
                        <span className="badge bg-amber-900/40 text-amber-200 border border-amber-800/50 text-[10px]">
                          not transferable
                        </span>
                      )}
                    </div>
                    {(t.keywords?.length > 0 || t.voicemailE164) && (
                      <p className="mt-1 text-sm text-stone-400">
                        {t.keywords?.length > 0 && <>matches: {t.keywords.join(', ')}</>}
                        {t.voicemailE164 && <> · vm: {t.voicemailE164}</>}
                      </p>
                    )}
                  </div>
                  {canWrite && (
                    <div className="shrink-0 flex items-center gap-1">
                      <button type="button" className="btn-ghost" onClick={() => startEdit(t)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-ghost text-red-300 hover:text-red-200"
                        onClick={() => remove(t.id)}
                        aria-label="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
